import React from 'react'
import styled, { css } from 'styled-components'
import { string, bool, func } from 'prop-types'
import { rgba, stripUnit } from 'polished'

import * as spacing from 'styles/spacing'
import { media } from 'styles/media'
import { greyLight, black, borderColor } from 'styles/colors'
import { square } from 'styles/global'
import { H1, H3, H4 } from 'styles/typography'

import Wrapper from 'components/Wrapper'
import Section from 'components/Section'
import Icon from 'components/Icon'

import LeftArrow from 'icons/left-arrow.svg'

const Container = styled.div`
  position: relative;
  text-align: center;
`

const Heading = styled(H3)`
  position: static;
  padding: 0 ${spacing.large};
  margin-bottom: ${stripUnit(spacing.small) * 1.5 + 'px'};

  ${media.medium`
    margin-bottom: ${stripUnit(spacing.small) * 1.5 + 'px'};
  `};
`

const LargeHeading = styled(H1)`
  font-family: 'Trim Poster';
  letter-spacing: 0.02em;
  color: ${black};
  margin-bottom: ${stripUnit(spacing.small) * 1.5 + 'px'};

  ${media.medium`
    margin-bottom: ${spacing.medium};
  `};

  ${media.large`
    font-size: 56px;
  `};

  ${media.xLarge`
    font-size: 72px;
  `};
`

const Description = styled.div`
  margin-bottom: ${spacing.medium};
  padding: 0 ${spacing.large};
  color: ${black};

  ${props =>
    props.isLarge &&
    css`
      position: static;

      ${media.small`
        font-size: 17px;
      `};

      ${media.medium`
        font-size: 20px;
      `};

      ${media.large`
        font-size: 22px;
      `};

      ${media.xLarge`
        font-size: 24px;
      `};
    `};

  &:last-child {
    margin-bottom: 0;
  }

  ${media.medium`
    margin-bottom: ${spacing.large};
    padding: 0;
  `};
`

const Inner = styled.div`
  max-width: 600px;
  margin: 0 auto;

  ${media.medium`
    max-width: 700px;
  `};

  ${media.large`
    max-width: 760px;
  `};

  ${media.xLarge`
    max-width: 840px;
  `};
`

const Price = styled(H4)`
  color: ${black};

  sub {
    bottom: 0;
    font-size: 65%;

    ${media.medium`
      font-size: 55%;
    `};
  }
`

const Compare = styled.sub`
  text-decoration: line-through;
  margin-right: ${spacing.small};
  color: ${greyLight};
`

const Back = styled.button`
  position: absolute;
  top: ${spacing.large};
  left: ${spacing.medium};
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background-color: ${rgba(borderColor, 0.75)};
  ${square('32px')};

  ${media.small`
    ${square('40px')};
  `};

  ${media.medium`
    display: none;
  `};
`

const CollectionHeader = ({ heading, isLarge, description, comparePrice, onBackClick, packPrice }) => {
  return (
    <Container>
      {onBackClick && (
        <Back onClick={onBackClick}>
          <Icon icon={<LeftArrow />} />
        </Back>
      )}
      <Section>
        <Wrapper>
          <Inner>
            {heading && isLarge ? <LargeHeading as="h1">{heading}</LargeHeading> : <Heading as="h1">{heading}</Heading>}
            {description && (
              <Description isLarge={isLarge}>
                <div dangerouslySetInnerHTML={{ __html: description }} />
              </Description>
            )}
            {packPrice && (
              <Price comparePrice={comparePrice}>
                {comparePrice && <Compare>{comparePrice}</Compare>}
                {packPrice}
                <sub>/pack</sub>
              </Price>
            )}
          </Inner>
        </Wrapper>
      </Section>
    </Container>
  )
}

CollectionHeader.propTypes = {
  heading: string,
  description: string,
  packPrice: string,
  comparePrice: string,
  isLarge: bool,
  onBackClick: func
}

export default CollectionHeader
